
import React from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { MovieProvider } from "@/context/movie/MovieProvider";
import { UndoToastProvider } from "@/components/toast/UndoToastProvider";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/reactQuery';
import { AuthProvider } from "@/context/auth";
import { SparkEffect } from "@/components/ui/spark";
import { SparkProvider } from "@/components/ui/spark/SparkContext";
import SparkDebugPanel from "@/components/ui/spark/SparkDebugPanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ErrorFallback from "@/components/ErrorFallback";
import { HelmetProvider } from "react-helmet-async";
import { MovieDialogProvider } from "@/context/dialog/MovieDialogContext";
import Index from "@/pages/Index";
import EditMovie from "@/pages/EditMovie";
import NotFound from "@/pages/NotFound";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import PasswordResetPage from "@/pages/PasswordResetPage";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

// Import error logger to initialize global error handlers
import "@/utils/errorLogger";

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary 
          fallback={
            <ErrorFallback 
              error={new Error("An unexpected error occurred")} 
              resetErrorBoundary={() => {
                console.log("🔄 [ERROR-BOUNDARY] Attempting graceful recovery instead of page reload");
                // Instead of window.location.reload(), we'll let the error boundary handle recovery
              }} 
            />
          }
        >
          <Router>
            <AuthProvider>
              <MovieProvider>
                <MovieDialogProvider>
                  <UndoToastProvider>
                    <SparkProvider>
                      <SparkEffect />
                      {process.env.NODE_ENV === 'development' && <SparkDebugPanel />}
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/auth/reset-password" element={<PasswordResetPage />} />
                        <Route path="/edit/:movieId" element={
                          <ProtectedRoute>
                            <EditMovie />
                          </ProtectedRoute>
                        } />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      <Toaster />
                    </SparkProvider>
                  </UndoToastProvider>
                </MovieDialogProvider>
              </MovieProvider>
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
